/**
 * @fileoverview Provides React hooks for accessing user-related data, including `useUserData` (for `User`), `useApiKeys` (for `ApiKey`), `useCreditInfo`, `useCreditTransactions`, `useMarketingConsent`, `useJobRuns` (for `IndexUrlJobRun`), `usePrivateIndex` (for `PrivateIndexRecord`), and `usePdfSets` (for `PdfSet`). Also includes utility functions for working with `UserTier` and `IndexUrlJobStatus`.
 */

import { useState, useEffect } from 'react'
import {
  doc,
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  limit,
  getDocs,
  getDoc,
} from 'firebase/firestore'
import { useAuth } from './useAuth'
import type { PdfSet, PdfSetStatus } from '../../shared-node/models/PdfSet'

export type UserTier = 'Free' | 'Basic' | 'Pro'

// Utility functions for working with tiers
export function isPaidTier(tier: UserTier): boolean {
  return tier !== 'Free'
}

export function hasBasicFeatures(tier: UserTier): boolean {
  return tier === 'Basic' || tier === 'Pro'
}

export function hasProFeatures(tier: UserTier): boolean {
  return tier === 'Pro'
}

export interface User {
  uid: string
  email: string
  displayName?: string
  photoURL?: string
  createdAt: Date
  credits: number
  tier: UserTier
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  subscriptionEndDate?: Date
  // Note: marketingConsent is now stored in a separate marketingConsent collection
}

export interface MarketingConsent {
  hasConsent: boolean
  updatedAt: Date
}

export interface ApiKey {
  id: string
  userId: string
  name: string
  key: string
  createdAt: Date
  lastUsed?: Date
  isActive: boolean
}

export interface CreditInfo {
  purchasedCredits: number
  monthlyAllowance: number
  monthlyCreditsUsed: number
  monthlyCreditsRemaining: number
  totalCreditsAvailable: number
}

export function useUserData() {
  const { user, loading: authLoading } = useAuth()
  const [userData, setUserData] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (authLoading) {
      setUserData(null)
      setLoading(true)
      return () => {}
    }

    if (!user) {
      setUserData(null)
      setLoading(false)
      return () => {}
    }

    const db = getFirestore()
    const userRef = doc(db, 'users', user.uid)

    const unsubscribe = onSnapshot(userRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data()
        // Convert timestamps to Date objects
        const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date()

        setUserData({
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || '',
          photoURL: user.photoURL || '',
          credits: data.credits || 0,
          tier: data.tier as UserTier,
          stripeCustomerId: data.stripeCustomerId,
          stripeSubscriptionId: data.stripeSubscriptionId,
          subscriptionEndDate: data.subscriptionEndDate?.toDate
            ? data.subscriptionEndDate.toDate()
            : undefined,
          createdAt,
        })
      } else {
        // User document doesn't exist yet, might still be getting created by auth trigger
        setUserData(null)
      }
      setLoading(false)
    })

    return unsubscribe
  }, [user, authLoading])

  return { userData, loading }
}
export function useApiKeys() {
  const { user } = useAuth()
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) {
      setApiKeys([])
      setLoading(false)
      return () => {}
    }

    const db = getFirestore()
    const keysQuery = query(collection(db, 'apiKeys'), where('userId', '==', user.uid))

    const unsubscribe = onSnapshot(
      keysQuery,
      {
        includeMetadataChanges: true,
        source: 'default', // Listen to both cache and server
      },
      (snapshot) => {
        const keys: ApiKey[] = []
        snapshot.forEach((doc) => {
          const data = doc.data()
          keys.push({
            id: doc.id,
            userId: data.userId,
            name: data.name,
            key: data.key,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
            lastUsed: data.lastUsed?.toDate ? data.lastUsed.toDate() : undefined,
            isActive: data.isActive,
          })
        })
        setApiKeys(keys)
        setLoading(false)
      },
      (error) => {
        console.error('Error listening to API keys:', error)
        setLoading(false)
      },
    )

    return unsubscribe
  }, [user])

  return { apiKeys, loading }
}

export function useCreditTransactions(limitCount = 100) {
  const { user } = useAuth()
  const [transactions, setTransactions] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) {
      return () => {}
    }

    const db = getFirestore()
    const transactionsQuery = query(
      collection(db, 'creditTransactions'),
      where('userId', '==', user.uid),
    )

    const unsubscribe = onSnapshot(transactionsQuery, (snapshot) => {
      const txns: any[] = []
      snapshot.forEach((doc) => {
        const data = doc.data()
        txns.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(),
        })
      })

      // Sort by timestamp descending
      txns.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())

      // Limit the number of transactions
      setTransactions(txns.slice(0, limitCount))
      setLoading(false)
    })

    return unsubscribe
  }, [user, limitCount])

  return { transactions, loading }
}

export function useCreditInfo() {
  const { user } = useAuth()
  const [creditInfo, setCreditInfo] = useState<CreditInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!user) {
      setCreditInfo(null)
      setLoading(false)
      return () => {}
    }

    const db = getFirestore()

    // Track loading state for both listeners
    let userDataLoaded = false
    let monthlyUsageLoaded = false
    let userData: any = null
    let monthlyCreditsUsed = 0

    const setLoadingComplete = () => {
      if (userDataLoaded && monthlyUsageLoaded) {
        setLoading(false)
      }
    }

    const calculateCreditInfo = () => {
      if (!userDataLoaded || !userData) return

      const credits = userData.credits || 0
      const monthlyAllowance = userData.monthlyAllowance || 0
      const tier = userData.tier as UserTier

      if (!hasBasicFeatures(tier) || monthlyAllowance <= 0) {
        setCreditInfo({
          purchasedCredits: credits,
          monthlyAllowance: 0,
          monthlyCreditsUsed: 0,
          monthlyCreditsRemaining: 0,
          totalCreditsAvailable: credits,
        })
      } else {
        const monthlyCreditsRemaining = Math.max(0, monthlyAllowance - monthlyCreditsUsed)
        setCreditInfo({
          purchasedCredits: credits,
          monthlyAllowance,
          monthlyCreditsUsed,
          monthlyCreditsRemaining,
          totalCreditsAvailable: credits + monthlyCreditsRemaining,
        })
      }
    }

    // Listen to user document changes
    const userRef = doc(db, 'users', user.uid)
    const unsubscribeUser = onSnapshot(userRef, (userDoc) => {
      try {
        if (!userDoc.exists()) {
          setCreditInfo(null)
          userDataLoaded = true
          setLoadingComplete()
          return
        }

        userData = userDoc.data()
        userDataLoaded = true
        calculateCreditInfo()
        setLoadingComplete()
        setError(null)
      } catch (err) {
        console.error('Error processing user data:', err)
        setError(err instanceof Error ? err : new Error(String(err)))
        userDataLoaded = true
        setLoadingComplete()
      }
    })

    // Listen to monthly usage changes
    let unsubscribeMonthlyUsage: (() => void) | null = null

    // Set up monthly usage listener after we have user data
    const setupMonthlyUsageListener = () => {
      if (!userData) return

      const now = new Date()
      const subscriptionStartDate = userData.subscriptionStartDate
        ? userData.subscriptionStartDate.toDate()
        : now

      const year = now.getFullYear()
      const month = now.getMonth()
      const day = subscriptionStartDate.getUTCDate()

      const monthlyUsageQuery = query(
        collection(db, 'monthlyUsage'),
        where('userId', '==', user.uid),
        where('year', '==', year),
        where('month', '==', month),
        where('day', '==', day),
        limit(1),
      )

      unsubscribeMonthlyUsage = onSnapshot(monthlyUsageQuery, (snapshot) => {
        try {
          monthlyCreditsUsed = 0
          if (!snapshot.empty) {
            monthlyCreditsUsed = snapshot.docs[0]?.data()?.creditsUsed || 0
          }

          monthlyUsageLoaded = true
          calculateCreditInfo()
          setLoadingComplete()
        } catch (err) {
          console.error('Error processing monthly usage data:', err)
          setError(err instanceof Error ? err : new Error(String(err)))
          monthlyUsageLoaded = true
          setLoadingComplete()
        }
      })
    }

    // Initial setup - we need to wait for user data first, then set up monthly usage listener
    const checkAndSetupMonthlyUsage = () => {
      if (userDataLoaded && userData && !unsubscribeMonthlyUsage) {
        setupMonthlyUsageListener()
      } else if (userDataLoaded && !userData) {
        // User data loaded but no user document exists
        monthlyUsageLoaded = true
        setLoadingComplete()
      }
    }

    // Check periodically if we need to set up the monthly usage listener
    const intervalId = setInterval(checkAndSetupMonthlyUsage, 100)

    // Cleanup function
    return () => {
      clearInterval(intervalId)
      unsubscribeUser()
      if (unsubscribeMonthlyUsage) {
        unsubscribeMonthlyUsage()
      }
    }
  }, [user])

  return { creditInfo, loading, error }
}

export function useMarketingConsent() {
  const { user, loading: authLoading } = useAuth()
  const [marketingConsent, setMarketingConsent] = useState<MarketingConsent | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (authLoading) {
      setMarketingConsent(null)
      setLoading(true)
      return () => {}
    }

    if (!user) {
      setMarketingConsent(null)
      setLoading(false)
      return () => {}
    }

    const db = getFirestore()
    const consentRef = doc(db, 'marketingConsent', user.uid)

    const unsubscribe = onSnapshot(consentRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data()
        const updatedAt = data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date()

        setMarketingConsent({
          hasConsent: data.hasConsent || false,
          updatedAt,
        })
      } else {
        // No marketing consent document exists yet - default to false
        setMarketingConsent({
          hasConsent: false,
          updatedAt: new Date(),
        })
      }
      setLoading(false)
    })

    return unsubscribe
  }, [user, authLoading])

  return { marketingConsent, loading }
}

// Job run types
export type IndexUrlJobStatus = 'CRAWLING' | 'INDEXING' | 'FAILED' | 'COMPLETE'

export interface IndexUrlJobRun {
  uid: string
  executionId: string
  startTime: Date
  endTime?: Date
  status: IndexUrlJobStatus
  url: string
  displayName: string
  stackName: string
  error?: string
  moduleId?: string
  docsCount?: number
}

export function useJobRuns(limitCount = 100) {
  const { user } = useAuth()
  const [jobRuns, setJobRuns] = useState<IndexUrlJobRun[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user) {
      setJobRuns([])
      setLoading(false)
      return () => {}
    }

    const db = getFirestore()
    const jobRunsQuery = query(collection(db, 'job_runs', user.uid, 'runs'))

    const unsubscribe = onSnapshot(jobRunsQuery, (snapshot) => {
      const runs: IndexUrlJobRun[] = []
      snapshot.forEach((doc) => {
        const data = doc.data()
        runs.push({
          uid: data.uid,
          executionId: doc.id,
          startTime: data.startTime?.toDate ? data.startTime.toDate() : new Date(),
          endTime: data.endTime?.toDate ? data.endTime.toDate() : undefined,
          status: data.status as IndexUrlJobStatus,
          url: data.url,
          displayName: data.displayName,
          stackName: data.stackName,
          error: data.error,
          moduleId: data.moduleId,
          docsCount: data.docsCount,
        })
      })

      // Sort by startTime descending (most recent first)
      runs.sort((a, b) => b.startTime.getTime() - a.startTime.getTime())

      // Limit the number of job runs
      setJobRuns(runs.slice(0, limitCount))
      setLoading(false)
    })

    return unsubscribe
  }, [user, limitCount])

  return { jobRuns, loading }
}

// Utility functions for working with job runs
export function isJobRunComplete(status: IndexUrlJobStatus): boolean {
  return status === 'COMPLETE' || status === 'FAILED'
}

export function isJobRunRunning(status: IndexUrlJobStatus): boolean {
  return status !== 'COMPLETE' && status !== 'FAILED'
}

export function getJobRunStatusColor(status: IndexUrlJobStatus): string {
  switch (status) {
    case 'COMPLETE':
      return 'status-complete'
    case 'FAILED':
      return 'status-failed'
    case 'CRAWLING':
    case 'INDEXING':
      return 'status-running'
    default:
      return 'status-unknown'
  }
}

export function getJobRunStatusIcon(status: IndexUrlJobStatus): string {
  switch (status) {
    case 'COMPLETE':
      return '✓'
    case 'FAILED':
      return '✗'
    case 'CRAWLING':
      return '🔍'
    case 'INDEXING':
      return '📝'
    default:
      return '?'
  }
}

// Private Index types and hooks
export interface PrivateIndexRecord {
  uid: string
  indexId: string
  stackName: string
  createdAt: Date
  updatedAt: Date
  totalDocs: number
  modules: Array<{
    id: string
    displayName: string
    url: string
    docsCount: number
    lastUpdated: Date
  }>
}

export function usePrivateIndex(indexId: string = 'default') {
  const { user } = useAuth()
  const [privateIndex, setPrivateIndex] = useState<PrivateIndexRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user) {
      setPrivateIndex(null)
      setLoading(false)
      return () => {}
    }

    const db = getFirestore()
    const indexRef = doc(db, 'privateIndex', user.uid, 'indexes', indexId)

    const unsubscribe = onSnapshot(indexRef, (doc) => {
      try {
        if (doc.exists()) {
          const data = doc.data()
          // Convert timestamps to Date objects
          const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date()
          const updatedAt = data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date()

          // Convert module timestamps
          const modules = (data.modules || []).map((module: any) => ({
            ...module,
            lastUpdated: module.lastUpdated?.toDate ? module.lastUpdated.toDate() : new Date(),
          }))

          setPrivateIndex({
            uid: data.uid,
            indexId: data.indexId,
            stackName: data.stackName,
            createdAt,
            updatedAt,
            totalDocs: data.totalDocs || 0,
            modules,
          })
        } else {
          setPrivateIndex(null)
        }
        setError(null)
        setLoading(false)
      } catch (err) {
        console.error('Error loading private index:', err)
        setError(err instanceof Error ? err.message : 'Failed to load private index')
        setLoading(false)
      }
    })

    return unsubscribe
  }, [user, indexId])

  return { privateIndex, loading, error }
}

// PDF Sets hook
export function usePdfSets() {
  const { user } = useAuth()
  const [pdfSets, setPdfSets] = useState<PdfSet[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user) {
      setPdfSets([])
      setLoading(false)
      return () => {}
    }

    const db = getFirestore()
    const pdfSetsQuery = query(collection(db, 'pdfSets', user.uid, 'sets'))

    const unsubscribe = onSnapshot(pdfSetsQuery, (snapshot) => {
      try {
        const sets: PdfSet[] = []
        snapshot.forEach((doc) => {
          const data = doc.data()

          // Convert timestamps to Date objects
          const uploadedAt = data.uploadedAt?.toDate ? data.uploadedAt.toDate() : new Date()
          const lastIndexedAt = data.lastIndexedAt?.toDate ? data.lastIndexedAt.toDate() : undefined

          // Convert PDF file timestamps
          const pdfFiles = (data.pdfFiles || []).map((file: any) => ({
            ...file,
            lastModified: file.lastModified?.toDate ? file.lastModified.toDate() : new Date(),
          }))

          sets.push({
            setId: doc.id,
            uid: data.uid,
            displayName: data.displayName,
            gcsFolder: data.gcsFolder,
            bucketName: data.bucketName,
            pdfFiles,
            totalFiles: data.totalFiles || 0,
            totalSizeBytes: data.totalSizeBytes || 0,
            status: data.status as PdfSetStatus,
            uploadedAt,
            lastIndexedAt,
            totalDocs: data.totalDocs,
            indexedPages: data.indexedPages,
            language: data.language,
            indexId: data.indexId,
            moduleId: data.moduleId,
            stackName: data.stackName,
            lastError: data.lastError,
            errorCount: data.errorCount || 0,
          })
        })

        // Sort by uploadedAt descending (most recent first)
        sets.sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime())

        setPdfSets(sets)
        setError(null)
        setLoading(false)
      } catch (err) {
        console.error('Error loading PDF sets:', err)
        setError(err instanceof Error ? err.message : 'Failed to load PDF sets')
        setLoading(false)
      }
    })

    return unsubscribe
  }, [user])

  return { pdfSets, loading, error }
}

// Utility functions for working with PDF sets
export function isPdfSetIndexed(status: PdfSetStatus): boolean {
  return status === 'indexed'
}

export function isPdfSetProcessing(status: PdfSetStatus): boolean {
  return status === 'indexing' || status === 'queued'
}

export function getPdfSetStatusColor(status: PdfSetStatus): string {
  switch (status) {
    case 'indexed':
      return 'status-complete'
    case 'failed':
      return 'status-failed'
    case 'indexing':
    case 'queued':
      return 'status-running'
    case 'not indexed':
      return 'status-pending'
    default:
      return 'status-unknown'
  }
}

export function getPdfSetStatusIcon(status: PdfSetStatus): string {
  switch (status) {
    case 'indexed':
      return '✓'
    case 'failed':
      return '✗'
    case 'indexing':
      return '📝'
    case 'queued':
      return '⏳'
    case 'not indexed':
      return '⭕'
    default:
      return '?'
  }
}
